import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, FileText, Loader2, Printer, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PdfCanvasViewer } from "@/components/pdf-canvas-viewer";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, ApiError, attachmentsZipUrl, downloadAttachmentFile, downloadUrlAsFile, getToken } from "@/lib/api";
import { apiUrl } from "@/apiOrigin";
import { useAuth } from "@/lib/auth";
import { orgTodayKey } from "@/lib/datetime";

interface ReportAttachment {
    id: string;
    file_name: string;
    created_at: string;
}

/**
 * Reports list with multi-select download, an in-app preview flyout, and an
 * admin-only regenerate action.
 *
 *  - Download: single selection saves the file directly (no new tab); a
 *    multi-selection streams a single zip from `/api/attachments/zip`.
 *  - Preview: a right-side Sheet renders the PDF inline via pdf.js canvas
 *    (not an iframe — iOS would hand an iframe'd PDF to Quick Look with no
 *    way back), with explicit Close, Download, and Print.
 *  - Edit (admin): re-runs the server-side regenerate-report orchestrator to
 *    rebuild the PDF after the underlying job data has been corrected.
 */
export function JobReportsList({
    title,
    reports,
    regenerateKind,
    jobId,
}: {
    title: string;
    reports: ReportAttachment[];
    /** Drives the admin regenerate endpoint; null hides the action. */
    regenerateKind: "backflow" | "fire-safety" | null;
    jobId: string;
}) {
    const { isAdmin } = useAuth();
    const qc = useQueryClient();
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [preview, setPreview] = useState<ReportAttachment | null>(null);
    const [busy, setBusy] = useState(false);

    const allSelected = reports.length > 0 && selected.size === reports.length;
    const someSelected = selected.size > 0;

    const toggle = (rid: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(rid)) next.delete(rid);
            else next.add(rid);
            return next;
        });
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(reports.map((r) => r.id)));

    const downloadSelected = async () => {
        const ids = [...selected].filter((id) => reports.some((r) => r.id === id));
        if (ids.length === 0) return;
        setBusy(true);
        try {
            if (ids.length === 1) {
                const r = reports.find((x) => x.id === ids[0]);
                await downloadAttachmentFile(ids[0], r?.file_name);
            } else {
                await downloadUrlAsFile(attachmentsZipUrl(ids), `reports-${orgTodayKey()}.zip`);
            }
        } catch {
            toast.error("Download failed");
        } finally {
            setBusy(false);
        }
    };

    const regenerate = useMutation({
        mutationFn: () => api.post(`/api/${regenerateKind}/job/${jobId}/regenerate-report`, {}),
        onSuccess: () => {
            toast.success("Report regenerated from current job data");
            qc.invalidateQueries({ queryKey: ["job", jobId, "test-reports"] });
            qc.invalidateQueries({ queryKey: ["job", jobId, "inspection-reports"] });
        },
        onError: (err) =>
            toast.error(err instanceof ApiError ? `Regenerate failed (${err.status})` : "Regenerate failed"),
    });

    if (reports.length === 0) return null;

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
                    {title} ({reports.length})
                </h4>
                <div className="flex items-center gap-2">
                    {isAdmin && regenerateKind && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={regenerate.isPending}
                            onClick={() => regenerate.mutate()}
                            title="Admin: rebuild the PDF from the current job data (edit device/asset values first, then regenerate)"
                        >
                            {regenerate.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            )}
                            Regenerate PDF
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={!someSelected || busy}
                        onClick={downloadSelected}
                    >
                        {busy ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                            <Download className="h-3.5 w-3.5 mr-1" />
                        )}
                        Download{selected.size > 1 ? ` (${selected.size}) as Zip` : ""}
                    </Button>
                </div>
            </div>
            <div className="rounded-lg border divide-y text-sm">
                <label className="flex items-center gap-2 px-3 py-1.5 bg-muted/30 text-xs text-foreground/70 cursor-pointer">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5" />
                    Select all
                </label>
                {reports.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/20 transition-colors">
                        <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggle(r.id)}
                            className="h-3.5 w-3.5 shrink-0"
                            aria-label={`Select ${r.file_name}`}
                        />
                        <FileText className="h-4 w-4 text-foreground/80 shrink-0" />
                        <button
                            type="button"
                            className="flex-1 min-w-0 truncate text-left hover:underline"
                            onClick={() => setPreview(r)}
                            title="Preview"
                        >
                            {r.file_name}
                        </button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 shrink-0"
                            onClick={() => setPreview(r)}
                            aria-label="Preview"
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 shrink-0"
                            onClick={() =>
                                downloadAttachmentFile(r.id, r.file_name).catch(() => toast.error("Download failed"))
                            }
                            aria-label="Download"
                        >
                            <Download className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>

            <AttachmentPreviewSheet attachment={preview} onClose={() => setPreview(null)} />
        </div>
    );
}

/**
 * Right-side flyout that renders an attachment PDF inline.
 *
 * Rendering goes through `PdfCanvasViewer` (pdf.js -> canvas), NOT a native
 * `<iframe>`: iOS WebKit hands an iframe'd PDF off to system Quick Look, which
 * has no in-app Close/Back and traps the user. Canvas keeps the document inside
 * the sheet, and the footer always offers a clear Close.
 */
export function AttachmentPreviewSheet({
    attachment,
    onClose,
}: {
    attachment: ReportAttachment | null;
    onClose: () => void;
}) {
    const open = attachment != null;
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!attachment) return;
        let cancelled = false;
        let createdUrl: string | null = null;
        setLoading(true);
        setError(null);
        setBlobUrl(null);
        setPdfData(null);
        const token = getToken();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        fetch(apiUrl(`/api/attachments/${attachment.id}/download?disposition=inline`), { headers })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
                const blob = await res.blob();
                if (cancelled) return;
                createdUrl = URL.createObjectURL(blob);
                setBlobUrl(createdUrl);
                setPdfData(await blob.arrayBuffer());
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : "Preview failed");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [attachment]);

    // Print without a persistent iframe: spin up a transient hidden frame from
    // the blob URL, print, then tear it down. Avoids leaving a visible iframe
    // (and the iOS Quick Look hand-off) on screen.
    const handlePrint = () => {
        if (!blobUrl) return;
        const frame = document.createElement("iframe");
        frame.style.position = "fixed";
        frame.style.right = "0";
        frame.style.bottom = "0";
        frame.style.width = "0";
        frame.style.height = "0";
        frame.style.border = "0";
        frame.src = blobUrl;
        frame.onload = () => {
            try {
                frame.contentWindow?.focus();
                frame.contentWindow?.print();
            } catch {
                /* ignore */
            }
            window.setTimeout(() => frame.remove(), 1000);
        };
        document.body.appendChild(frame);
    };

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="flex w-[min(96vw,820px)] flex-col sm:max-w-none p-0">
                <SheetHeader className="pr-12">
                    <SheetTitle className="truncate text-sm">{attachment?.file_name ?? "Preview"}</SheetTitle>
                </SheetHeader>
                <div className="flex-1 bg-muted/40 min-h-0">
                    {loading && (
                        <div className="flex h-full items-center justify-center text-sm text-foreground/70">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering preview…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="flex h-full items-center justify-center text-sm text-destructive">{error}</div>
                    )}
                    {pdfData && !error && !loading && (
                        <PdfCanvasViewer data={pdfData} className="relative flex h-full w-full flex-col" />
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 border-t bg-card px-4 py-3">
                    <Button variant="outline" size="sm" onClick={onClose}>
                        <X className="mr-1.5 h-4 w-4" /> Close
                    </Button>
                    {attachment && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                downloadAttachmentFile(attachment.id, attachment.file_name).catch(() =>
                                    toast.error("Download failed"),
                                )
                            }
                        >
                            <Download className="mr-1.5 h-4 w-4" /> Download
                        </Button>
                    )}
                    <Button size="sm" onClick={handlePrint} disabled={!blobUrl}>
                        <Printer className="mr-1.5 h-4 w-4" /> Print
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
